/* ============================================================================
   VILLAINS TCG — Hero carousel (cst-hero.js)
   Crossfade slideshow with dots. Autoplay runs automatically and pauses only
   while hovered/focused (accessibility) and is disabled under reduced-motion.
   No visible pause control. Keyboard arrows navigate. Progressive enhancement.
   ========================================================================== */

const Base = (window.CST && window.CST.Element) || HTMLElement;

class CstHeroCarousel extends Base {
  mount() {
    this.slides = this.refs('[data-hero-slide]');
    this.dots = this.refs('[data-hero-dot]');
    this.index = 0;
    this.speed = (parseFloat(this.dataset.autoplay) || 0) * 1000;
    if (this.slides.length < 2) return;

    this.dots.forEach((d, i) => this.listen(d, 'click', () => { this.go(i); this.restart(); }));

    this.listen(this, 'pointerenter', () => this.suspend());
    this.listen(this, 'pointerleave', () => this.resume());
    this.listen(this, 'focusin', () => this.suspend());
    this.listen(this, 'focusout', (e) => { if (!this.contains(e.relatedTarget)) this.resume(); });
    this.listen(this, 'keydown', (e) => {
      if (e.key === 'ArrowRight') { this.go(this.index + 1); this.restart(); }
      else if (e.key === 'ArrowLeft') { this.go(this.index - 1); this.restart(); }
    });

    // swipe / drag (touch + mouse) — horizontal-dominant gestures change slides
    this.listen(this, 'pointerdown', (e) => {
      // ignore secondary pointers (multi-touch) and gestures that begin on an
      // interactive control, so a slightly-draggy CTA tap can't also flip slides
      if (!e.isPrimary || e.target.closest('a, button')) { this._swiping = false; return; }
      this._sx = e.clientX; this._sy = e.clientY; this._swiping = true;
    });
    this.listen(this, 'pointerup', (e) => {
      if (!this._swiping) return;
      this._swiping = false;
      const dx = e.clientX - this._sx;
      const dy = e.clientY - this._sy;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) { this.go(dx < 0 ? this.index + 1 : this.index - 1); this.restart(); }
    });

    this.resume();
  }

  go(i) {
    const n = this.slides.length;
    i = ((i % n) + n) % n;
    this.slides.forEach((s, x) => {
      const on = x === i;
      s.classList.toggle('is-current', on);
      if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
    });
    this.dots.forEach((d, x) => {
      const on = x === i;
      d.classList.toggle('is-on', on);
      d.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    this.index = i;
  }

  next() { this.go(this.index + 1); }

  /* autoplay lifecycle */
  resume() { this.suspend(); if (this.speed && !this.reducedMotion) this.timer = setInterval(() => this.next(), this.speed); }
  restart() { this.resume(); }
  suspend() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }

  unmount() { this.suspend(); }
}

if (window.CST && window.CST.defineElement) window.CST.defineElement('cst-hero-carousel', CstHeroCarousel);
else if (!customElements.get('cst-hero-carousel')) customElements.define('cst-hero-carousel', CstHeroCarousel);
